import { create } from 'zustand';

/**
 * Outcome of a gate session, awaited by the 403 KYC_REQUIRED backstop so it can
 * decide whether to replay the action that triggered the gate.
 * - `approved`  — KYC reached APPROVED while the gate was open → resume the action.
 * - `dismissed` — the user closed the gate → let the original error propagate.
 */
export type KycGateOutcome = 'approved' | 'dismissed';

interface KycGateState {
  /** Flow key the gate should run, or null when closed. */
  activeFlowKey: string | null;
  /** Bumps each time something requests the gate — lets the modal reset its
   *  internal state (token, error) even if the same flow is re-opened. */
  openToken: number;
  /** Resolvers for callers awaiting the in-flight gate session (the 403 backstop).
   *  A list, not a single resolver, so several gated requests that fired before
   *  approval all resume together. */
  _resolvers: ((outcome: KycGateOutcome) => void)[];

  /** Open the gate for a flow and wait for its outcome (approved / dismissed). */
  openFlow: (flowKey: string) => Promise<KycGateOutcome>;
  /** KYC reached APPROVED for the active flow — close and resume awaiting callers. */
  approve: () => void;
  /** User dismissed the gate (✕ / backdrop) — close and reject the action's resume. */
  close: () => void;
}

const useKycGateStore = create<KycGateState>((set, get) => ({
  activeFlowKey: null,
  openToken: 0,
  _resolvers: [],

  openFlow: (flowKey) => {
    let resolve!: (outcome: KycGateOutcome) => void;
    const promise = new Promise<KycGateOutcome>((r) => {
      resolve = r;
    });
    set((s) => ({ activeFlowKey: flowKey, openToken: s.openToken + 1, _resolvers: [...s._resolvers, resolve] }));
    return promise;
  },

  approve: () => {
    const { _resolvers } = get();
    set({ activeFlowKey: null, _resolvers: [] });
    _resolvers.forEach((resolve) => resolve('approved'));
  },

  close: () => {
    const { _resolvers } = get();
    set({ activeFlowKey: null, _resolvers: [] });
    _resolvers.forEach((resolve) => resolve('dismissed'));
  },
}));

/** The flow the gate is currently running, or null. */
export const useKycActiveFlowKey = () => useKycGateStore((s) => s.activeFlowKey);

/** Increments whenever the gate is (re)opened — used as a remount key. */
export const useKycOpenToken = () => useKycGateStore((s) => s.openToken);

/** Close the gate (hook form). Resolves the awaiting action as `dismissed`. */
export const useCloseKycGate = () => useKycGateStore((s) => s.close);

/** Approve the gate (hook form). Resolves the awaiting action as `approved`. */
export const useApproveKycGate = () => useKycGateStore((s) => s.approve);

/** Open the gate for a flow (hook form). */
export const useOpenKycGate = () => useKycGateStore((s) => s.openFlow);

/** Non-hook accessor for the 403 interceptor (outside React). Awaits the outcome. */
export const openKycGateImperative = (flowKey: string): Promise<KycGateOutcome> =>
  useKycGateStore.getState().openFlow(flowKey);

/** Non-hook close (tests, imperative teardown). */
export const closeKycGateImperative = () => useKycGateStore.getState().close();
